/**
 * Define las transiciones Svelte para elementos que se montan y desmontan con if, como
 * modales y avisos. Necesitan una transición para completar la salida
 * antes de retirar el nodo.
 *
 * Las duraciones se leen de los tokens --mo-* de styles/animations.css para mantener el
 * mismo ritmo que las animaciones CSS. Cada curva Bézier indica el token cuya forma
 * reproduce.
 */
import { prefersReducedMotion } from "svelte/motion";
import type { TransitionConfig } from "svelte/transition";

/* ------------------------------------------------------------------ */
/* Los tokens, leídos de la hoja                                        */
/* ------------------------------------------------------------------ */

const cache = new Map<string, number>();

/**
 * Lo que dura un token del sistema, en milisegundos.
 *
 * Se lee una vez por token y se guarda: son constantes de diseño, no cambian
 * en caliente, y `getComputedStyle` en cada entrada de un modal costaría un
 * recálculo de estilo justo cuando hay que empezar a dibujar.
 */
function ms(token: string, fallback: number): number {
  const hit = cache.get(token);
  if (hit !== undefined) return hit;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
  const value = raw.endsWith("ms")
    ? Number.parseFloat(raw)
    : raw.endsWith("s")
      ? Number.parseFloat(raw) * 1000
      : Number.NaN;
  const out = Number.isFinite(value) && value > 0 ? value : fallback;
  cache.set(token, out);
  return out;
}

/* ------------------------------------------------------------------ */
/* Las curvas                                                          */
/* ------------------------------------------------------------------ */

/**
 * Una bézier de CSS, evaluable.
 *
 * Svelte muestrea `css(t)` con `t` lineal, así que el perfil lo tiene que
 * poner la función: sin esto, todo saldría a velocidad constante.
 */
function bezier(x1: number, y1: number, x2: number, y2: number) {
  const eje = (a: number, b: number, t: number) =>
    3 * (1 - t) ** 2 * t * a + 3 * (1 - t) * t ** 2 * b + t ** 3;
  return (x: number) => {
    let lo = 0;
    let hi = 1;
    // Bisección; doce pasos dejan el error muy por debajo de un píxel.
    for (let i = 0; i < 12; i++) {
      const medio = (lo + hi) / 2;
      if (eje(x1, x2, medio) < x) lo = medio;
      else hi = medio;
    }
    return eje(y1, y2, (lo + hi) / 2);
  };
}

/** Copia de `--mo-enter`: frena casi del todo antes de llegar. */
const entrar = bezier(0.16, 1, 0.3, 1);
/** Copia de `--mo-leave`: acelera al irse. */
const salir = bezier(0.4, 0, 1, 1);
/** Copia de `--mo-move`: la de ida y vuelta, y la de los fundidos. */
const mover = bezier(0.4, 0, 0.2, 1);

/**
 * Copia de `--mo-spring`: el muelle amortiguado de la hoja (ζ=0.75, ω=9). Se
 * pasa un 2,8% de su destino y vuelve; es lo que le da peso a lo que se abre.
 */
function muelle(t: number): number {
  const z = 0.75;
  const w = 9;
  const wd = w * Math.sqrt(1 - z * z);
  return 1 - Math.exp(-z * w * t) * (Math.cos(wd * t) + ((z * w) / wd) * Math.sin(wd * t));
}

/* ------------------------------------------------------------------ */
/* Las medidas                                                         */
/* ------------------------------------------------------------------ */

/** De qué tamaño crece lo que entra (`--mo-shrink`). */
const CHICA = 0.97;
/** Cuántos rem sube (`--mo-lift`). */
const SUBE = 0.625;
/** Cuántos rem entra de lado lo que viene del borde (`--mo-travel`). */
const VIENE = 1;

/**
 * El resto del panel apaga el movimiento con `@media (prefers-reduced-motion)`;
 * aquí no hay CSS que apagar --lo dibuja Svelte-- y la misma preferencia se
 * lee desde JavaScript.
 */
const quieto = () => prefersReducedMotion.current;

/*
 * El nodo no se usa en ninguna de las seis, pero el directivo las llama con el
 * elemento delante: sin declararlo, TypeScript cuenta un argumento de más.
 */
type Transicion = (nodo: Element) => TransitionConfig;

/* ------------------------------------------------------------------ */
/* Superficies: el modal y la hoja                                     */
/* ------------------------------------------------------------------ */

/**
 * Estilo de una superficie en un punto de su entrada o salida: opacidad, escala y desplazamiento.
 */
const superficie = (p: number, opacidad: number) =>
  `opacity: ${opacidad}; scale: ${CHICA + (1 - CHICA) * p}; translate: 0 ${SUBE * (1 - p)}rem;`;

/**
 * Como entra el `.modal` del catálogo: subiendo `--mo-lift` y creciendo desde
 * `--mo-shrink`, con el muelle. La opacidad acaba antes que el movimiento,
 * para que el último tramo --el lento, el que se mira-- se vea ya nítido.
 */
export const surfaceIn: Transicion = () => {
  const duracion = ms("--mo-t-surface", 336);
  const opaca = duracion * 0.65;
  return {
    duration: quieto() ? 0 : duracion,
    css: (t) => superficie(muelle(t), mover(Math.min(t / (opaca / duracion), 1))),
  };
};

/** Y como se va: corto y acelerando, que quitarse de en medio no se mira. */
export const surfaceOut: Transicion = () => ({
  duration: quieto() ? 0 : ms("--mo-t-leave", 180),
  // `t` baja de 1 a 0, así que lo que se curva es `1 - t`: el tiempo corrido.
  css: (t) => {
    const p = 1 - salir(1 - t);
    return superficie(p, p);
  },
});

/* ------------------------------------------------------------------ */
/* Velos                                                               */
/* ------------------------------------------------------------------ */

/** El velo entra antes que la tarjeta: lo de detrás se apaga primero. */
export const veilIn: Transicion = () => ({
  duration: quieto() ? 0 : ms("--mo-t-surface", 336) * 0.8,
  css: (t) => `opacity: ${mover(t)}`,
});

/** Y se va con ella. */
export const veilOut: Transicion = () => ({
  duration: quieto() ? 0 : ms("--mo-t-leave", 180),
  css: (t) => `opacity: ${1 - salir(1 - t)}`,
});

/* ------------------------------------------------------------------ */
/* Avisos                                                              */
/* ------------------------------------------------------------------ */

/**
 * El aviso de la esquina. Viene del borde por el que se queda --lateral, no
 * de abajo-- porque es lo único del panel que vive pegado a una esquina: el
 * recorrido dice de dónde sale sin tener que mirar.
 */
export const noticeIn: Transicion = () => {
  const duracion = ms("--mo-t-surface", 336);
  return {
    duration: quieto() ? 0 : duracion,
    css: (t) => {
      const p = entrar(t);
      return `opacity: ${mover(Math.min(t * 2, 1))}; translate: ${VIENE * (1 - p)}rem 0; scale: ${
        CHICA + (1 - CHICA) * p
      };`;
    },
  };
};

/**
 * Al irse se encoge en vertical además de desvanecerse: los que quedan
 * debajo suben a ocupar su sitio sin dar un salto.
 */
export const noticeOut: Transicion = () => ({
  duration: quieto() ? 0 : ms("--mo-t-leave", 180),
  css: (t) => {
    const p = 1 - salir(1 - t);
    return `opacity: ${p}; translate: ${VIENE * (1 - p)}rem 0; scale: ${0.96 + 0.04 * p};`;
  },
});
